/**
 * Recovery for stale dynamic-import (chunk) failures after a deploy.
 *
 * When a new build ships, a browser still holding the previous single-page-app
 * shell can request hashed chunk files that the new build has deleted. The
 * dynamic import then fails ("Failed to fetch dynamically imported module") and
 * the target route renders blank. Vite raises a `vite:preloadError` event for
 * exactly this case, and a single full page reload fetches the fresh shell and
 * resolves it.
 *
 * The guard is a timestamp in storage with a cooldown: a reload is allowed only
 * when the previous automatic reload was longer ago than the cooldown. That
 * recovers from deploy staleness (deploys are minutes or hours apart) while
 * refusing to loop when a chunk is genuinely broken (which would fail again
 * immediately, well inside the cooldown).
 */

/** Storage key holding the timestamp (milliseconds) of the last automatic reload. */
export const PRELOAD_RELOAD_AT_KEY = 'altar:preload-reload-at'

/** Minimum time between two automatic reloads. Shorter gaps are treated as a loop. */
export const PRELOAD_RELOAD_COOLDOWN_MS = 10_000

/**
 * Whether a reload is allowed now, given when the last automatic reload happened.
 * The comparison is inclusive so a reload exactly one cooldown later is allowed.
 *
 * @param options.lastReloadAt - Timestamp (ms) of the previous automatic reload, or 0 if none.
 * @param options.now - The current time (ms).
 * @param options.cooldownMs - Minimum gap between reloads (ms).
 * @returns True when enough time has passed to reload again.
 */
export const shouldReloadOnPreloadError = ({
  lastReloadAt,
  now,
  cooldownMs = PRELOAD_RELOAD_COOLDOWN_MS,
}: {
  lastReloadAt: number
  now: number
  cooldownMs?: number
}): boolean => now - lastReloadAt >= cooldownMs

/**
 * Handle a `vite:preloadError`: record the attempt and reload once, unless a
 * reload already happened inside the cooldown window (in which case the chunk is
 * presumed genuinely broken and we stop, rather than reload-looping).
 *
 * @param options.storage - A Web Storage instance (localStorage) for the loop guard.
 * @param options.now - The current time (ms); injected so the guard is testable.
 * @param options.reload - Performs the page reload.
 * @param options.cooldownMs - Minimum gap between reloads (ms).
 */
export const handlePreloadError = ({
  storage,
  now,
  reload,
  cooldownMs = PRELOAD_RELOAD_COOLDOWN_MS,
}: {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  now: number
  reload: () => void
  cooldownMs?: number
}): void => {
  const lastReloadAt = Number(storage.getItem(PRELOAD_RELOAD_AT_KEY) ?? '0')
  if (!shouldReloadOnPreloadError({ lastReloadAt, now, cooldownMs })) {
    return
  }
  storage.setItem(PRELOAD_RELOAD_AT_KEY, String(now))
  reload()
}
